// The full syntax of files in a document body:
//
//   image            ![name](trameria://file/<uuid>)
//   image (width)    ![name](trameria://file/<uuid>?w=320)
//   video            ![name](trameria://file/<uuid>?kind=video)
//   video (width)    ![name](trameria://file/<uuid>?kind=video&w=480)
//   anything else    [name](trameria://file/<uuid>)
//
// The basic line (no width) is defined once in `@trameria/core/files-shared`
// (`fileMarkdownLine`, `escapeFileName`); this module adds what only bodies
// have - widths, reading the syntax back - on top of it.

import { escapeFileName } from "@trameria/core/files-shared";

/** A uuid (no anchors: tokenizers embed it). */
export const FILE_UUID_SRC =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

/**
 * A name. The only two escapes are `\\` and `\]`; accepting others (`\q`)
 * would change characters on unescape and break exact round trips.
 */
export const FILE_NAME_SRC = "(?:[^\\]\\\\\\n]|\\\\[\\\\\\]])*";

/**
 * The query in the URL (`?w=320` / `?kind=video` / `?kind=video&w=320`).
 * Matches anything up to `)`: {@link parseFileQuery} interprets it. If an odd
 * query (`?w=abc`) made the token fail, the line would not become a node and
 * the file id would be lost from the body.
 */
export const FILE_QUERY_SRC = "(?:\\?[^)\\s]*)?";

/** The narrowest width (px); narrower, the resize handles cover the image. */
export const FILE_WIDTH_MIN = 80;

/**
 * The body column width (px): wider would only overflow, so it is the widest.
 * There is no CSS variable for it; update it with the body column.
 */
export const FILE_WIDTH_MAX = 696;

const FILE_UUID_RE = new RegExp(`^${FILE_UUID_SRC}$`);

/** The attributes of a file node. */
export interface TrameriaFileAttrs {
  /** Lowercase uuid. */
  fileId: string;
  /** The name shown (alt of an image). Held by the body, apart from the file's own name. */
  name: string;
  /** Drawn as an image (the `!`). */
  image: boolean;
  /** Width in px; null is the default. Images only (a chip's width follows its name). */
  width?: number | null;
  /** Drawn as a video (`kind=video`). Only with `image`; never written on chips. */
  video?: boolean;
}

export function isFileId(v: unknown): v is string {
  return typeof v === "string" && FILE_UUID_RE.test(v);
}

/** Clamps an integer width to {@link FILE_WIDTH_MIN}..{@link FILE_WIDTH_MAX}. */
export function clampFileWidth(px: number): number {
  return Math.min(Math.max(Math.round(px), FILE_WIDTH_MIN), FILE_WIDTH_MAX);
}

/**
 * The one reading of a width, from the body, the DOM or a drag (if the node
 * view and the static view read it differently, the width would change when
 * the view switches). Integer px only: `w=abc`, `w=`, `w=1.5`, negatives and 0
 * give null (the default). Positive values out of range are clamped, so a body
 * pasted from elsewhere still works.
 */
export function normalizeFileWidth(raw: unknown): number | null {
  const text =
    typeof raw === "number"
      ? Number.isInteger(raw)
        ? String(raw)
        : ""
      : typeof raw === "string"
        ? raw.trim()
        : "";
  if (!/^\d+$/.test(text)) return null;
  const px = Number(text);
  if (px <= 0) return null;
  return clampFileWidth(px);
}

/** Width and video flag from a query (`?kind=video&w=320`). Unknown keys are dropped. */
export function parseFileQuery(raw: string | null | undefined): {
  width: number | null;
  video: boolean;
} {
  let width: number | null = null;
  let video = false;
  for (const part of (raw ?? "").replace(/^\?/, "").split("&")) {
    const eq = part.indexOf("=");
    const key = eq < 0 ? part : part.slice(0, eq);
    const value = eq < 0 ? "" : part.slice(eq + 1);
    if (key === "w") width = normalizeFileWidth(value);
    else if (key === "kind") video = value === "video";
  }
  return { width, video };
}

export function unescapeFileName(raw: string): string {
  return raw.replace(/\\([\\\]])/g, "$1");
}

/**
 * The body line of a file node. An id that is not a uuid writes nothing (the
 * default `fileId: ""` would leave an unreadable `[](trameria://file/)`). The
 * width is written only when set: writing the default would rewrite every
 * untouched body. Chips never get a width or the video flag. Order: kind, then w.
 */
export function fileNodeToRaw(attrs: TrameriaFileAttrs): string {
  if (!isFileId(attrs.fileId)) return "";
  const bang = attrs.image ? "!" : "";
  const params: string[] = [];
  if (attrs.image && attrs.video) params.push("kind=video");
  const width = attrs.image ? normalizeFileWidth(attrs.width) : null;
  if (width !== null) params.push(`w=${width}`);
  const query = params.length > 0 ? `?${params.join("&")}` : "";
  return `${bang}[${escapeFileName(attrs.name)}](trameria://file/${attrs.fileId.toLowerCase()}${query})`;
}
